import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { superAdminAPI } from '../../services/api';
import {
  FilterSelect, ReportFilterBar, EMPTY_REPORT_FILTERS, filterReportRows, uniqueSorted,
} from '../../components/SuperAdmin/ListTools';
import { formatExportDate } from '../../utils/exportData';
import {
  HiOutlineOfficeBuilding,
  HiOutlineRefresh,
  HiOutlineTrendingUp,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineClock,
  HiOutlineCalendar,
} from 'react-icons/hi';
import { ReportGuide, periodLabel } from '../../components/SuperAdmin/ReportGuide';

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', year: 'numeric' }) : '—';

const PERIODS = [7, 30, 60, 90, 180, 365];

const OUTCOME_STYLES = {
  renewed: 'bg-green-100 text-green-700',
  missed: 'bg-red-100 text-red-700',
  upcoming: 'bg-blue-100 text-blue-700',
};

const RenewalConversionAnalytics = () => {
  const [days, setDays] = useState(30);

  const { data, isLoading, isFetching, refetch } = useQuery({
    queryKey: ['renewal-conversion', days],
    queryFn: () => superAdminAPI.getRenewalConversion({ days }),
    refetchInterval: 120000,
  });

  // Axios → res.data (server body) → body.data
  const body = data?.data?.data || {};
  const stats = body.stats || {};
  const allEvents = Array.isArray(body.events) ? body.events : [];
  const [filters, setFilters] = useState({ ...EMPTY_REPORT_FILTERS, outcome: '', plan: '', source: '' });
  const events = filterReportRows(allEvents, filters, {
    searchOf: (e) => [e.organizationName, e.organizationEmail, e.organizationPhone, e.planName],
  }).filter((e) =>
    (!filters.outcome || e.outcome === filters.outcome)
    && (!filters.plan || e.planName === filters.plan)
    && (!filters.source || (e.sources || []).includes(filters.source)));

  const conversionRate = stats.conversionRate ?? 0;
  const decided = (stats.renewed ?? 0) + (stats.missed ?? 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex-1 basis-80">
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineTrendingUp className="w-7 h-7 text-primary-500" />
            Renewal Success Rate
          </h1>
          <p className="text-sm text-gray-500 mt-1">
            Out of the subscriptions that ended, how many customers renewed
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-3">
          <span className="text-xs font-medium text-gray-500">Show last</span>
          <div className="flex flex-wrap gap-1 bg-gray-100 rounded-xl p-1">
            {PERIODS.map((p) => (
              <button
                key={p}
                onClick={() => setDays(p)}
                className={`px-3.5 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  days === p ? 'bg-white shadow text-gray-900' : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                {periodLabel(p)}
              </button>
            ))}
          </div>
          <button
            onClick={() => refetch()}
            disabled={isFetching}
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-500 text-white text-sm font-medium
                       hover:bg-primary-600 shadow-lg shadow-primary-500/25 disabled:opacity-50"
          >
            <HiOutlineRefresh className={`w-4 h-4 ${isFetching ? 'animate-spin' : ''}`} />
            Refresh
          </button>
        </div>
      </div>

      <ReportGuide
        id="renewal-conversion"
        summary="Shows how good we are at keeping customers when their subscription ends."
        points={[
          ["Conversion rate", "Renewed ÷ (renewed + missed). Higher is better."],
          ["Expiring", "Subscriptions that ended in the selected period."],
          ["Renewed", "Customers who paid and continued."],
          ["Missed", "Customers who did not renew."],
          ["Source", "How the renewal was handled (for example a follow-up task)."],
        ]}
      />

      {/* Conversion rate hero + KPI tiles */}
      <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
        {/* Conversion rate */}
        <div className="bg-white rounded-xl border border-gray-200 p-5 lg:row-span-1">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider">Conversion Rate</p>
          {decided === 0 ? (
            <p className="text-4xl font-bold text-gray-300 mt-2">—</p>
          ) : (
            <p className={`text-4xl font-bold mt-2 ${conversionRate >= 70 ? 'text-green-600' : conversionRate >= 40 ? 'text-amber-500' : 'text-red-500'}`}>
              {conversionRate}%
            </p>
          )}
          {/* Simple bar: green = renewed share of decided events */}
          <div className="mt-3 h-2.5 rounded-full bg-gray-100 overflow-hidden">
            <div
              className="h-full bg-green-500 rounded-full transition-all"
              style={{ width: decided === 0 ? '0%' : `${Math.round(((stats.renewed ?? 0) / decided) * 100)}%` }}
            />
          </div>
          <p className="text-xs text-gray-400 mt-2">
            {stats.renewed ?? 0} of {decided} decided renewal{decided !== 1 ? 's' : ''} (last {body.windowDays ?? days} days)
          </p>
        </div>

        {[
          { label: 'Expiring', value: stats.expiring ?? 0, icon: HiOutlineCalendar, color: 'text-primary-500' },
          { label: 'Renewed', value: stats.renewed ?? 0, icon: HiOutlineCheckCircle, color: 'text-green-500' },
          { label: 'Missed', value: stats.missed ?? 0, icon: HiOutlineXCircle, color: 'text-red-500' },
        ].map((t) => {
          const Icon = t.icon;
          return (
            <div key={t.label} className="bg-white rounded-xl border border-gray-200 p-5 flex items-center gap-4">
              <div className="w-11 h-11 rounded-lg bg-gray-50 flex items-center justify-center">
                <Icon className={`w-5 h-5 ${t.color}`} />
              </div>
              <div>
                <p className="text-2xl font-bold text-gray-900">{t.value}</p>
                <p className="text-xs text-gray-500">{t.label}</p>
              </div>
            </div>
          );
        })}
      </div>

      {/* Events table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 bg-gray-50">
          <h2 className="text-sm font-semibold text-gray-700">Expiring Subscriptions (last {body.windowDays ?? days} days)</h2>
          <span className="text-xs text-gray-400">
            {events.length === allEvents.length ? `${events.length} shown` : `${events.length} of ${allEvents.length} shown`}
          </span>
        </div>

        {allEvents.length > 0 && (
          <ReportFilterBar
            filters={filters}
            onChange={setFilters}
            showPriority={false}
            searchPlaceholder="Search organization, email, phone, plan…"
            extraActive={!!(filters.outcome || filters.plan || filters.source)}
            onResetExtra={() => setFilters((f) => ({ ...f, outcome: '', plan: '', source: '' }))}
            exportProps={{
              fileName: `renewal-conversion-${days}d`,
              getRows: () => events,
              columns: [
                { header: 'Organization', value: (e) => e.organizationName },
                { header: 'Email', value: (e) => e.organizationEmail },
                { header: 'Phone', value: (e) => e.organizationPhone },
                { header: 'Plan', value: (e) => e.planName },
                { header: 'Expiry Date', value: (e) => formatExportDate(e.dueDate, false) },
                { header: 'Source', value: (e) => e.sources || [] },
                { header: 'Outcome', value: (e) => e.outcome },
                { header: 'Current Expiry', value: (e) => (e.outcome === 'renewed' ? formatExportDate(e.currentDueDate, false) : '') },
              ],
            }}
          >
            <FilterSelect value={filters.outcome} onChange={(v) => setFilters((f) => ({ ...f, outcome: v }))}
              allLabel="All Outcomes" options={[['renewed', 'Renewed'], ['missed', 'Missed'], ['upcoming', 'Upcoming']]} />
            <FilterSelect value={filters.plan} onChange={(v) => setFilters((f) => ({ ...f, plan: v }))}
              allLabel="All Plans" options={uniqueSorted(allEvents.map((e) => e.planName)).map((p) => [p, p])} />
            <FilterSelect value={filters.source} onChange={(v) => setFilters((f) => ({ ...f, source: v }))}
              allLabel="All Sources" options={uniqueSorted(allEvents.flatMap((e) => e.sources || [])).map((s) => [s, s])} />
          </ReportFilterBar>
        )}

        {isLoading ? (
          <div className="p-8 text-center text-gray-400 text-sm">Loading analytics…</div>
        ) : events.length === 0 ? (
          <div className="p-10 text-center text-sm text-gray-400">
            {allEvents.length > 0 ? 'No subscriptions match these filters' : 'No expiring subscriptions in this period'}
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="border-b border-gray-200 text-left text-xs uppercase tracking-wider text-gray-500">
                  <th className="px-4 py-3">Organization</th>
                  <th className="px-4 py-3">Phone Number</th>
                  <th className="px-4 py-3">Plan</th>
                  <th className="px-4 py-3">Expiry Date</th>
                  <th className="px-4 py-3">Source</th>
                  <th className="px-4 py-3">Outcome</th>
                  <th className="px-4 py-3">Current Expiry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {events.map((ev) => (
                  <tr key={`${ev.organizationId}-${ev.dueDate}`} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <div className="flex items-start gap-2">
                        <HiOutlineOfficeBuilding className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                        <div className="min-w-0">
                          <span className="font-medium text-gray-900">{ev.organizationName}</span>
                          {ev.organizationEmail && (
                            <p className="text-xs text-gray-400 truncate max-w-[200px]" title={ev.organizationEmail}>
                              {ev.organizationEmail}
                            </p>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-gray-600 whitespace-nowrap">
                      {ev.organizationPhone || <span className="text-gray-400">—</span>}
                    </td>
                    <td className="px-4 py-3 text-gray-600">{ev.planName || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{formatDate(ev.dueDate)}</td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        {(ev.sources || []).map((s) => (
                          <span
                            key={s}
                            className="px-2 py-0.5 rounded-full text-[10px] font-semibold bg-gray-100 text-gray-500 capitalize"
                          >
                            {s}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2.5 py-1 rounded-full text-xs font-semibold capitalize ${OUTCOME_STYLES[ev.outcome] || 'bg-gray-100 text-gray-500'}`}>
                        {ev.outcome}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-gray-600">
                      {ev.outcome === 'renewed' ? formatDate(ev.currentDueDate) : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default RenewalConversionAnalytics;